// SPDX-FileCopyrightText: Copyright 2025 New Vector Ltd.
// SPDX-FileCopyrightText: Copyright 2025, 2026 Element Creations Ltd.
//
// SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial

import { FormattedMessage, useIntl } from "react-intl";

import { assertNever } from "@/utils/never";

import styles from "./logo.module.css";

/** The full E-Chat logo. */
export const ElementLogotype: React.FC = () => {
  const intl = useIntl();
  const title = intl.formatMessage({
    id: "product.element",
    description: "Product name.",
    defaultMessage: "E-Chat",
  });

  return (
    <svg
      width="74"
      height="16"
      viewBox="0 0 74 16"
      fill="none"
      // oxlint-disable-next-line jsx-a11y/prefer-tag-over-role
      role="img"
      aria-label={title}
      xmlns="http://www.w3.org/2000/svg"
    >
      <title>{title}</title>
      <ElementLogoPart width="16" height="16" aria-hidden="true" />
      <ElementTextPart x="21" y="0" width="53" height="16" aria-hidden="true" />
    </svg>
  );
};

type Variant = "pro" | "community" | "ti-m";

interface ElementLogoProps extends React.ComponentProps<"div"> {
  variant: Variant;
}

/** The Element logo, reconstructed in three parts: the round logo, the text part and the badge */
export const ElementLogo: React.FC<ElementLogoProps> = ({
  variant,
  ...props
}: ElementLogoProps) => {
  const intl = useIntl();
  let title;
  let BadgePart;
  switch (variant) {
    case "pro": {
      title = intl.formatMessage({
        id: "product.title.pro",
        defaultMessage: "E-Chat Pro",
        description: "The main name of the admin console, the Pro variant",
      });
      BadgePart = ProBadgeText;
      break;
    }
    case "community": {
      title = intl.formatMessage({
        id: "product.title.community",
        defaultMessage: "E-Chat Community",
        description:
          "The main name of the admin console, the Community variant",
      });
      BadgePart = CommunityBadgeText;
      break;
    }
    case "ti-m": {
      title = intl.formatMessage({
        id: "product.title.ti-m",
        defaultMessage: "E-Chat TI-Messenger",
        description:
          "The main name of the admin console, the TI-Messenger variant",
      });
      BadgePart = TIMBadgeText;
      break;
    }

    default: {
      assertNever(variant);
    }
  }

  return (
    <div
      className={styles["element-logo-container"]}
      // oxlint-disable-next-line jsx-a11y/prefer-tag-over-role
      role="img"
      aria-label={title}
      data-variant={variant}
      {...props}
    >
      <ElementLogoPart aria-hidden="true" />
      <ElementTextPart aria-hidden="true" />
      <div className={styles["badge"]} aria-hidden="true">
        <BadgePart />
      </div>
    </div>
  );
};

export const ProBadge: React.FC = () => {
  const intl = useIntl();
  const title = intl.formatMessage({
    id: "product.badge.pro",
    description:
      "Accessible label for the badge marking a card as a feature only available in ESS Pro",
    defaultMessage: "Pro",
  });
  return (
    <div data-variant="pro">
      <div className={styles["badge"]}>
        <ProBadgeText
          // oxlint-disable-next-line jsx-a11y/prefer-tag-over-role
          role="img"
          aria-label={title}
        />
      </div>
    </div>
  );
};

/** The "PRO" text for the badge */
const ProBadgeText: React.FC<React.ComponentProps<"svg">> = (props) => (
  <svg
    width="29"
    height="10"
    viewBox="-2 0 29 10"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    {...props}
  >
    <path
      d="M1.46 4.796H3.548C4.012 4.796 4.356 4.688 4.58 4.472C4.812 4.256 4.928 3.956 4.928 3.572C4.928 3.188 4.812 2.884 4.58 2.66C4.348 2.436 4.004 2.324 3.548 2.324H1.46V4.796ZM1.46 6.14V9.5H0.00800008V0.98H3.536C4.504 0.98 5.22 1.22 5.684 1.7C6.156 2.172 6.392 2.796 6.392 3.572C6.392 4.34 6.152 4.96 5.672 5.432C5.192 5.904 4.48 6.14 3.536 6.14H1.46ZM14.2589 9.5H12.5549L10.8269 6.14H8.87094V9.5H7.41894V0.98H11.1629C12.0749 0.98 12.7709 1.216 13.2509 1.688C13.7309 2.16 13.9709 2.784 13.9709 3.56C13.9709 4.144 13.8309 4.648 13.5509 5.072C13.2709 5.488 12.8549 5.784 12.3029 5.96L14.2589 9.5ZM12.1709 4.46C12.3949 4.236 12.5069 3.936 12.5069 3.56C12.5069 3.184 12.3949 2.884 12.1709 2.66C11.9469 2.436 11.6149 2.324 11.1749 2.324H8.87094V4.796H11.1749C11.6149 4.796 11.9469 4.684 12.1709 4.46ZM17.0652 3.032C16.5292 3.592 16.2612 4.328 16.2612 5.24C16.2612 6.152 16.5292 6.888 17.0652 7.448C17.6012 8.008 18.2892 8.288 19.1292 8.288C19.9692 8.288 20.6572 8.008 21.1932 7.448C21.7292 6.888 21.9972 6.152 21.9972 5.24C21.9972 4.328 21.7292 3.592 21.1932 3.032C20.6572 2.472 19.9692 2.192 19.1292 2.192C18.2892 2.192 17.6012 2.472 17.0652 3.032ZM15.9612 8.42C15.1372 7.572 14.7252 6.512 14.7252 5.24C14.7252 3.968 15.1372 2.912 15.9612 2.072C16.7852 1.224 17.8412 0.8 19.1292 0.8C20.4172 0.8 21.4732 1.224 22.2972 2.072C23.1212 2.912 23.5332 3.968 23.5332 5.24C23.5332 6.512 23.1212 7.572 22.2972 8.42C21.4732 9.26 20.4172 9.68 19.1292 9.68C17.8412 9.68 16.7852 9.26 15.9612 8.42Z"
      fill="currentColor"
    />
  </svg>
);

/** The "Community" text for the badge */
const CommunityBadgeText: React.FC<React.ComponentProps<"svg">> = (props) => (
  <svg
    width="68"
    height="13"
    viewBox="-1 -1 68 13"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    {...props}
  >
    <path
      d="M0.54 5.228C0.54 3.924 0.936 2.86 1.728 2.036C2.528 1.212 3.552 0.8 4.8 0.8C5.864 0.8 6.768 1.096 7.512 1.688C8.256 2.28 8.716 3.08 8.892 4.088H7.344C7.2 3.496 6.9 3.032 6.444 2.696C5.996 2.36 5.448 2.192 4.8 2.192C3.952 2.192 3.284 2.468 2.796 3.02C2.316 3.564 2.076 4.3 2.076 5.228C2.076 6.156 2.316 6.896 2.796 7.448C3.284 8 3.952 8.276 4.8 8.276C5.448 8.276 5.996 8.108 6.444 7.772C6.9 7.436 7.2 6.972 7.344 6.38H8.892C8.716 7.388 8.256 8.188 7.512 8.78C6.768 9.372 5.864 9.668 4.8 9.668C3.552 9.668 2.528 9.256 1.728 8.432C0.936 7.608 0.54 6.54 0.54 5.228ZM12.7356 9.644C11.7756 9.644 10.9956 9.328 10.3956 8.696C9.80356 8.064 9.50756 7.296 9.50756 6.392C9.50756 5.488 9.80356 4.72 10.3956 4.088C10.9956 3.448 11.7756 3.128 12.7356 3.128C13.7036 3.128 14.4836 3.448 15.0756 4.088C15.6756 4.72 15.9756 5.488 15.9756 6.392C15.9756 7.296 15.6756 8.064 15.0756 8.696C14.4836 9.328 13.7036 9.644 12.7356 9.644ZM10.9356 6.392C10.9356 6.984 11.1036 7.464 11.4396 7.832C11.7836 8.2 12.2156 8.384 12.7356 8.384C13.2636 8.384 13.6956 8.2 14.0316 7.832C14.3756 7.464 14.5476 6.984 14.5476 6.392C14.5476 5.792 14.3756 5.308 14.0316 4.94C13.6956 4.572 13.2636 4.388 12.7356 4.388C12.2156 4.388 11.7836 4.572 11.4396 4.94C11.1036 5.308 10.9356 5.792 10.9356 6.392ZM22.4224 5.924V9.5H20.9944V5.768C20.9944 4.824 20.6024 4.352 19.8184 4.352C19.3944 4.352 19.0544 4.488 18.7984 4.76C18.5504 5.032 18.4264 5.404 18.4264 5.876V9.5H16.9984V3.272H18.3184V4.1C18.4704 3.82 18.7024 3.588 19.0144 3.404C19.3264 3.22 19.7144 3.128 20.1784 3.128C21.0424 3.128 21.6664 3.456 22.0504 4.112C22.5784 3.456 23.2824 3.128 24.1624 3.128C24.8904 3.128 25.4504 3.356 25.8424 3.812C26.2344 4.26 26.4304 4.852 26.4304 5.588V9.5H25.0024V5.768C25.0024 4.824 24.6104 4.352 23.8264 4.352C23.3944 4.352 23.0504 4.492 22.7944 4.772C22.5464 5.044 22.4224 5.428 22.4224 5.924ZM33.22 5.924V9.5H31.792V5.768C31.792 4.824 31.4 4.352 30.616 4.352C30.192 4.352 29.852 4.488 29.596 4.76C29.348 5.032 29.224 5.404 29.224 5.876V9.5H27.796V3.272H29.116V4.1C29.268 3.82 29.5 3.588 29.812 3.404C30.124 3.22 30.512 3.128 30.976 3.128C31.84 3.128 32.464 3.456 32.848 4.112C33.376 3.456 34.08 3.128 34.96 3.128C35.688 3.128 36.248 3.356 36.64 3.812C37.032 4.26 37.228 4.852 37.228 5.588V9.5H35.8V5.768C35.8 4.824 35.408 4.352 34.624 4.352C34.192 4.352 33.848 4.492 33.592 4.772C33.344 5.044 33.22 5.428 33.22 5.924ZM42.6137 6.86V3.272H44.0417V9.5H42.7217V8.696C42.5777 8.968 42.3417 9.196 42.0137 9.38C41.6937 9.556 41.2977 9.644 40.8257 9.644C40.0897 9.644 39.5217 9.424 39.1217 8.984C38.7297 8.536 38.5337 7.94 38.5337 7.196V3.272H39.9617V7.004C39.9617 7.948 40.3657 8.42 41.1737 8.42C41.6217 8.42 41.9737 8.288 42.2297 8.024C42.4857 7.752 42.6137 7.364 42.6137 6.86ZM46.7856 3.272V4.1C46.9296 3.828 47.1656 3.6 47.4936 3.416C47.8296 3.224 48.2336 3.128 48.7056 3.128C49.4416 3.128 50.0096 3.352 50.4096 3.8C50.8176 4.248 51.0216 4.844 51.0216 5.588V9.5H49.5936V5.768C49.5936 5.328 49.4896 4.984 49.2816 4.736C49.0816 4.48 48.7736 4.352 48.3576 4.352C47.9016 4.352 47.5416 4.488 47.2776 4.76C47.0216 5.032 46.8936 5.408 46.8936 5.888V9.5H45.4656V3.272H46.7856ZM51.772 4.544V3.272H54.112V9.5H52.684V4.544H51.772ZM53.404 2.228C53.14 2.228 52.924 2.148 52.756 1.988C52.588 1.82 52.504 1.608 52.504 1.352C52.504 1.088 52.588 0.872 52.756 0.704C52.924 0.536 53.14 0.452 53.404 0.452C53.66 0.452 53.872 0.536 54.04 0.704C54.208 0.872 54.292 1.088 54.292 1.352C54.292 1.608 54.208 1.82 54.04 1.988C53.872 2.148 53.66 2.228 53.404 2.228ZM58.3373 8.228V9.464C58.1613 9.512 57.9133 9.536 57.5933 9.536C56.3773 9.536 55.7693 8.924 55.7693 7.7V4.412H54.8213V3.272H55.7693V1.652H57.1973V3.272H58.3613V4.412H57.1973V7.556C57.1973 8.044 57.4293 8.288 57.8933 8.288L58.3373 8.228ZM59.8503 11.996L60.9063 9.452L58.3983 3.272H59.9223L61.6143 7.784L63.3303 3.272H64.8183L61.2663 11.996H59.8503Z"
      fill="currentColor"
    />
  </svg>
);

/** The "TI-Messenger" text for the badge */
const TIMBadgeText: React.FC<React.ComponentProps<"svg">> = (props) => (
  <svg
    width="77"
    height="13"
    viewBox="-1 -1 77 13"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    {...props}
  >
    <path
      d="M6.468 0.98V2.36H3.996V9.5H2.544V2.36H0.072V0.98H6.468ZM6.65878 2.288V0.98H9.86278V2.288H8.98678V8.192H9.86278V9.5H6.65878V8.192H7.53478V2.288H6.65878ZM14.3143 5.72V7.052H10.4983V5.72H14.3143ZM16.8853 0.98L19.5733 4.508L22.2733 0.98H23.6053V9.5H22.1773V3.272L19.5973 6.608H19.5493L16.9813 3.272V9.5H15.5533V0.98H16.8853ZM30.9117 6.8H26.1717C26.2277 7.296 26.4077 7.692 26.7117 7.988C27.0157 8.276 27.4157 8.42 27.9117 8.42C28.2397 8.42 28.5357 8.34 28.7997 8.18C29.0637 8.02 29.2517 7.804 29.3637 7.532H30.8037C30.6117 8.164 30.2517 8.676 29.7237 9.068C29.2037 9.452 28.5877 9.644 27.8757 9.644C26.9477 9.644 26.1957 9.336 25.6197 8.72C25.0517 8.104 24.7677 7.324 24.7677 6.38C24.7677 5.46 25.0557 4.688 25.6317 4.064C26.2077 3.44 26.9517 3.128 27.8637 3.128C28.7757 3.128 29.5117 3.436 30.0717 4.052C30.6397 4.66 30.9237 5.428 30.9237 6.356L30.9117 6.8ZM27.8637 4.292C27.4157 4.292 27.0437 4.424 26.7477 4.688C26.4517 4.952 26.2677 5.304 26.1957 5.744H29.5077C29.4437 5.304 29.2677 4.952 28.9797 4.688C28.6917 4.424 28.3197 4.292 27.8637 4.292ZM34.0664 9.644C33.2104 9.644 32.5664 9.444 32.1344 9.044C31.7024 8.644 31.4664 8.132 31.4264 7.508H32.8064C32.8224 7.796 32.9464 8.028 33.1784 8.204C33.4104 8.38 33.7344 8.468 34.1504 8.468C34.5184 8.468 34.8024 8.404 35.0024 8.276C35.2024 8.148 35.3024 7.976 35.3024 7.76C35.3024 7.52 35.1784 7.344 34.9304 7.232C34.6904 7.112 34.3944 7.024 34.0424 6.968C33.6904 6.912 33.3384 6.84 32.9864 6.752C32.6344 6.656 32.3344 6.472 32.0864 6.2C31.8464 5.928 31.7264 5.556 31.7264 5.084C31.7264 4.54 31.9504 4.08 32.3984 3.704C32.8544 3.32 33.4544 3.128 34.1984 3.128C34.6224 3.128 34.9944 3.196 35.3144 3.332C35.6424 3.46 35.8944 3.632 36.0704 3.848C36.2544 4.064 36.3904 4.284 36.4784 4.508C36.5744 4.732 36.6304 4.96 36.6464 5.192H35.2784C35.2544 4.944 35.1464 4.732 34.9544 4.556C34.7704 4.38 34.5064 4.292 34.1624 4.292C33.8184 4.292 33.5544 4.36 33.3704 4.496C33.1864 4.624 33.0944 4.804 33.0944 5.036C33.0944 5.252 33.2144 5.416 33.4544 5.528C33.7024 5.64 34.0024 5.724 34.3544 5.78C34.7064 5.836 35.0584 5.916 35.4104 6.02C35.7624 6.116 36.0584 6.304 36.2984 6.584C36.5464 6.864 36.6704 7.236 36.6704 7.7C36.6704 8.26 36.4424 8.724 35.9864 9.092C35.5384 9.46 34.8984 9.644 34.0664 9.644ZM39.6609 9.644C38.8049 9.644 38.1609 9.444 37.7289 9.044C37.2969 8.644 37.0609 8.132 37.0209 7.508H38.4009C38.4169 7.796 38.5409 8.028 38.7729 8.204C39.0049 8.38 39.3289 8.468 39.7449 8.468C40.1129 8.468 40.3969 8.404 40.5969 8.276C40.7969 8.148 40.8969 7.976 40.8969 7.76C40.8969 7.52 40.7729 7.344 40.5249 7.232C40.2849 7.112 39.9889 7.024 39.6369 6.968C39.2849 6.912 38.9329 6.84 38.5809 6.752C38.2289 6.656 37.9289 6.472 37.6809 6.2C37.4409 5.928 37.3209 5.556 37.3209 5.084C37.3209 4.54 37.5449 4.08 37.9929 3.704C38.4489 3.32 39.0489 3.128 39.7929 3.128C40.2169 3.128 40.5889 3.196 40.9089 3.332C41.2369 3.46 41.4889 3.632 41.6649 3.848C41.8489 4.064 41.9849 4.284 42.0729 4.508C42.1689 4.732 42.2249 4.96 42.2409 5.192H40.8729C40.8489 4.944 40.7409 4.732 40.5489 4.556C40.3649 4.38 40.1009 4.292 39.7569 4.292C39.4129 4.292 39.1489 4.36 38.9649 4.496C38.7809 4.624 38.6889 4.804 38.6889 5.036C38.6889 5.252 38.8089 5.416 39.0489 5.528C39.2969 5.64 39.5969 5.724 39.9489 5.78C40.3009 5.836 40.6529 5.916 41.0049 6.02C41.3569 6.116 41.6529 6.304 41.8929 6.584C42.1409 6.864 42.2649 7.236 42.2649 7.7C42.2649 8.26 42.0369 8.724 41.5809 9.092C41.1329 9.46 40.4929 9.644 39.6609 9.644ZM48.9844 6.8H44.2444C44.3004 7.296 44.4804 7.692 44.7844 7.988C45.0884 8.276 45.4884 8.42 45.9844 8.42C46.3124 8.42 46.6084 8.34 46.8724 8.18C47.1364 8.02 47.3244 7.804 47.4364 7.532H48.8764C48.6844 8.164 48.3244 8.676 47.7964 9.068C47.2764 9.452 46.6604 9.644 45.9484 9.644C45.0204 9.644 44.2684 9.336 43.6924 8.72C43.1244 8.104 42.8404 7.324 42.8404 6.38C42.8404 5.46 43.1284 4.688 43.7044 4.064C44.2804 3.44 45.0244 3.128 45.9364 3.128C46.8484 3.128 47.5844 3.436 48.1444 4.052C48.7124 4.66 48.9964 5.428 48.9964 6.356L48.9844 6.8ZM45.9364 4.292C45.4884 4.292 45.1164 4.424 44.8204 4.688C44.5244 4.952 44.3404 5.304 44.2684 5.744H47.5804C47.5164 5.304 47.3404 4.952 47.0524 4.688C46.7644 4.424 46.3924 4.292 45.9364 4.292ZM51.3231 3.272V4.1C51.4671 3.828 51.7031 3.6 52.0311 3.416C52.3671 3.224 52.7711 3.128 53.2431 3.128C53.9791 3.128 54.5471 3.352 54.9471 3.8C55.3551 4.248 55.5591 4.844 55.5591 5.588V9.5H54.1311V5.768C54.1311 5.328 54.0271 4.984 53.8191 4.736C53.6191 4.48 53.3111 4.352 52.8951 4.352C52.4391 4.352 52.0791 4.488 51.8151 4.76C51.5591 5.032 51.4311 5.408 51.4311 5.888V9.5H50.0031V3.272H51.3231ZM61.4695 4.16V3.272H62.7895V9.236C62.7895 10.084 62.5255 10.776 61.9975 11.312C61.4775 11.856 60.7495 12.128 59.8135 12.128C58.9575 12.128 58.2855 11.908 57.7975 11.468C57.3175 11.028 57.0375 10.524 56.9575 9.956H58.3855C58.5615 10.588 59.0455 10.904 59.8375 10.904C60.3175 10.904 60.6895 10.768 60.9535 10.496C61.2255 10.232 61.3615 9.876 61.3615 9.428V8.456C60.9455 9.096 60.2895 9.416 59.3935 9.416C58.5295 9.416 57.8335 9.116 57.3055 8.516C56.7855 7.908 56.5255 7.16 56.5255 6.272C56.5255 5.384 56.7855 4.64 57.3055 4.04C57.8335 3.432 58.5295 3.128 59.3935 3.128C60.3455 3.128 61.0375 3.472 61.4695 4.16ZM61.4215 6.272C61.4215 5.72 61.2615 5.268 60.9415 4.916C60.6215 4.564 60.2015 4.388 59.6815 4.388C59.1695 4.388 58.7535 4.564 58.4335 4.916C58.1135 5.268 57.9535 5.72 57.9535 6.272C57.9535 6.816 58.1135 7.268 58.4335 7.628C58.7535 7.98 59.1695 8.156 59.6815 8.156C60.2015 8.156 60.6215 7.98 60.9415 7.628C61.2615 7.268 61.4215 6.816 61.4215 6.272ZM69.9047 6.8H65.1647C65.2207 7.296 65.4007 7.692 65.7047 7.988C66.0087 8.276 66.4087 8.42 66.9047 8.42C67.2327 8.42 67.5287 8.34 67.7927 8.18C68.0567 8.02 68.2447 7.804 68.3567 7.532H69.7967C69.6047 8.164 69.2447 8.676 68.7167 9.068C68.1967 9.452 67.5807 9.644 66.8687 9.644C65.9407 9.644 65.1887 9.336 64.6127 8.72C64.0447 8.104 63.7607 7.324 63.7607 6.38C63.7607 5.46 64.0487 4.688 64.6247 4.064C65.2007 3.44 65.9447 3.128 66.8567 3.128C67.7687 3.128 68.5047 3.436 69.0647 4.052C69.6327 4.66 69.9167 5.428 69.9167 6.356L69.9047 6.8ZM66.8567 4.292C66.4087 4.292 66.0367 4.424 65.7407 4.688C65.4447 4.952 65.2607 5.304 65.1887 5.744H68.5007C68.4367 5.304 68.2607 4.952 67.9727 4.688C67.6847 4.424 67.3127 4.292 66.8567 4.292ZM74.2834 3.224V4.556C74.1714 4.54 74.0634 4.532 73.9594 4.532C72.8874 4.532 72.3514 5.108 72.3514 6.26V9.5H70.9234V3.272H72.2434V4.136C72.5954 3.52 73.2074 3.212 74.0794 3.212L74.2834 3.224Z"
      fill="currentColor"
    />
  </svg>
);

/** The text part of the Element logo */
const ElementTextPart: React.FC<React.ComponentProps<"svg">> = (props) => (
  <svg
    width="79"
    height="22"
    viewBox="0 0 79 22"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    {...props}
  >
    <text
      x="0"
      y="18"
      fill="currentColor"
      fontFamily="Inter, Arial, sans-serif"
      fontSize="22"
      fontWeight="600"
    >
      <FormattedMessage
        id="product.element"
        description="Product name."
        defaultMessage="E-Chat"
      />
    </text>
  </svg>
);

/** The logo part of the Element logo */
const ElementLogoPart: React.FC<React.ComponentProps<"svg">> = (props) => (
  <svg
    width="32"
    height="32"
    viewBox="0 0 72 72"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    {...props}
  >
    <path
      fill="#DE7247"
      d="M14 0H58Q72 0 72 14V58Q72 72 58 72H14Q0 72 0 58V14Q0 0 14 0Z"
    />
    <g transform="translate(14.4 18.288) scale(0.432)">
      <path
        fill="#FEFCF6"
        d="M15 0H68C75 0 80 5.5 80 12C80 18.5 75 24 68 24H30Q22 24 22 32V36C22 44 16 50 8 50H2.5Q0 50 0 47.5V15C0 6.7 6.7 0 15 0Z M85 82H32C25 82 20 76.5 20 70C20 63.5 25 58 32 58H70Q78 58 78 50V46C78 38 84 32 92 32H97.5Q100 32 100 34.5V67C100 75.3 93.3 82 85 82Z"
      />
    </g>
  </svg>
);

/** "Element Server Suite" logo, but vertical */
export const EssLogotypeVertical: React.FC = () => {
  const intl = useIntl();
  const title = intl.formatMessage({
    id: "product.title",
    defaultMessage: "E-Chat Admin",
    description: "The main name of the admin console",
  });
  return (
    <svg
      width="301"
      height="102"
      viewBox="0 0 301 102"
      fill="none"
      // oxlint-disable-next-line jsx-a11y/prefer-tag-over-role
      role="img"
      aria-label={title}
      className="max-w-full h-auto"
      xmlns="http://www.w3.org/2000/svg"
    >
      <title>{title}</title>
      <ElementLogoPart
        x="122"
        y="0"
        width="56"
        height="56"
        aria-hidden="true"
      />
      <ElementTextPart
        x="91"
        y="66"
        width="119"
        height="33"
        aria-hidden="true"
      />
    </svg>
  );
};
